import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { FormattedMoodboardItem } from '../../../hooks/queries/moodboardTypes';

interface ImageModalProps {
  image: FormattedMoodboardItem;
  onClose: () => void;
}

export function ImageModal({ image, onClose }: ImageModalProps) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const calculateDimensions = () => {
      // Get viewport dimensions with padding
      const viewportWidth = window.innerWidth - 32; // 16px padding on each side
      const viewportHeight = window.innerHeight - 32;

      // Calculate aspect ratios
      const imageRatio = image.width / image.height;
      const viewportRatio = viewportWidth / viewportHeight;

      let width, height;

      if (imageRatio > viewportRatio) {
        // Image is wider than viewport ratio
        width = Math.min(image.width, viewportWidth);
        height = width / imageRatio;
      } else {
        // Image is taller than viewport ratio
        height = Math.min(image.height, viewportHeight);
        width = height * imageRatio;
      }

      setDimensions({ width, height });
    };

    calculateDimensions();

    // Recalculate on window resize
    window.addEventListener('resize', calculateDimensions);
    return () => window.removeEventListener('resize', calculateDimensions);
  }, [image.width, image.height]);

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-4 z-50 flex items-center justify-center" onClick={onClose}>
        {/* Close button */}
        <button
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
        >
          <X size={24} />
        </button>

        {/* Image container */}
        <div
          className="relative flex items-center justify-center"
          onClick={e => e.stopPropagation()}
        >
          <img
            src={image.url}
            alt=""
            className="max-w-full max-h-full object-contain"
            style={{
              width: dimensions.width,
              height: dimensions.height,
            }}
          />
        </div>
      </div>
    </>,
    document.body
  );
}
